import { ISubmissionVehicleResponse } from '../../../../model/ser/submission/response/submission-vehicle-response';
import { RejectedFile, SendStep, SubmissionDraft, createDraft, defaultVehicleIds, duplicateDraft, firstInvalidStep, withDefaultVehicles } from '../submission-send-rules';

export interface SubmissionSendResult {
    submissionIds: number[];
    batchId?: number;
}

export type SubmissionSendState = {
    drafts: SubmissionDraft[];
    activeKey: string;
    activeStep: SendStep;
    // Etapa mais avançada já alcançada por remessa (permite voltar e avançar até ela pelo stepper)
    reached: Record<string, SendStep>;
    // Exibe os erros de validação da etapa atual (após tentar avançar)
    showErrors: boolean;
    rejectedFiles: RejectedFile[];
    vehicles: ISubmissionVehicleResponse[];
    vehiclesLoading: boolean;
    sending: boolean;
    progress: number;
    generalErrors: string[];
    result?: SubmissionSendResult;
};

export enum ACTION_TYPES {
    updateDraft = 'updateDraft',
    setDraftVehicles = 'setDraftVehicles',
    addDraft = 'addDraft',
    duplicateDraft = 'duplicateDraft',
    removeDraft = 'removeDraft',
    selectDraft = 'selectDraft',
    setStep = 'setStep',
    setShowErrors = 'setShowErrors',
    setRejectedFiles = 'setRejectedFiles',
    setVehiclesLoading = 'setVehiclesLoading',
    setVehicles = 'setVehicles',
    sendStart = 'sendStart',
    setProgress = 'setProgress',
    sendSuccess = 'sendSuccess',
    sendError = 'sendError',
    reset = 'reset',
}

export function createInitialState(): SubmissionSendState {
    const draft = createDraft();
    return {
        drafts: [draft],
        activeKey: draft.key,
        activeStep: SendStep.Data,
        reached: { [draft.key]: SendStep.Data },
        showErrors: false,
        rejectedFiles: [],
        vehicles: [],
        vehiclesLoading: false,
        sending: false,
        progress: 0,
        generalErrors: [],
    };
}

// Abre a remessa na primeira etapa pendente (ou na Revisão quando completa)
function activate(state: SubmissionSendState, draft: SubmissionDraft): SubmissionSendState {
    const step = firstInvalidStep(draft);
    return {
        ...state,
        activeKey: draft.key,
        activeStep: step,
        reached: { ...state.reached, [draft.key]: Math.max(state.reached[draft.key] ?? SendStep.Data, step) },
        showErrors: false,
        rejectedFiles: [],
    };
}

// Formulário em branco mantendo os veículos carregados (e o diário próprio já marcado)
function resetWithVehicles(vehicles: ISubmissionVehicleResponse[]): SubmissionSendState {
    const initial = createInitialState();
    return { ...initial, vehicles, drafts: initial.drafts.map((d) => ({ ...d, vehicleIds: defaultVehicleIds(vehicles) })) };
}

export const SubmissionSendReducer = (state: SubmissionSendState, action: any): SubmissionSendState => {
    const { type, payload } = action;
    switch (type) {
        case ACTION_TYPES.updateDraft:
            // Editar a remessa limpa o erro devolvido pela API para ela
            return {
                ...state,
                drafts: state.drafts.map((d) => (d.key === state.activeKey ? { ...d, ...payload, apiErrors: [] } : d)),
            };
        case ACTION_TYPES.setDraftVehicles:
            return {
                ...state,
                drafts: state.drafts.map((d) => (d.key === payload.key ? { ...d, vehicleIds: payload.vehicleIds } : d)),
            };
        case ACTION_TYPES.addDraft: {
            const draft = { ...createDraft(), vehicleIds: defaultVehicleIds(state.vehicles) };
            return {
                ...state,
                drafts: [...state.drafts, draft],
                activeKey: draft.key,
                activeStep: SendStep.Data,
                reached: { ...state.reached, [draft.key]: SendStep.Data },
                showErrors: false,
                rejectedFiles: [],
            };
        }
        case ACTION_TYPES.duplicateDraft: {
            const index = state.drafts.findIndex((d) => d.key === payload);
            if (index < 0) return state;
            const copy = duplicateDraft(state.drafts[index]);
            const drafts = [...state.drafts];
            drafts.splice(index + 1, 0, copy);
            return activate({ ...state, drafts }, copy);
        }
        case ACTION_TYPES.removeDraft: {
            const index = state.drafts.findIndex((d) => d.key === payload);
            if (index < 0) return state;
            if (state.drafts.length === 1) return resetWithVehicles(state.vehicles);
            const drafts = state.drafts.filter((d) => d.key !== payload);
            const { [payload]: _removed, ...reached } = state.reached;
            const next = { ...state, drafts, reached };
            return payload === state.activeKey ? activate(next, drafts[Math.min(index, drafts.length - 1)]) : next;
        }
        case ACTION_TYPES.selectDraft: {
            const draft = state.drafts.find((d) => d.key === payload);
            return draft ? activate(state, draft) : state;
        }
        case ACTION_TYPES.setStep:
            return {
                ...state,
                activeStep: payload,
                reached: { ...state.reached, [state.activeKey]: Math.max(state.reached[state.activeKey] ?? SendStep.Data, payload) },
                showErrors: false,
            };
        case ACTION_TYPES.setShowErrors:
            return { ...state, showErrors: payload };
        case ACTION_TYPES.setRejectedFiles:
            return { ...state, rejectedFiles: payload };
        case ACTION_TYPES.setVehiclesLoading:
            return { ...state, vehiclesLoading: payload };
        case ACTION_TYPES.setVehicles: {
            // Diário próprio primeiro; ao trocar de entidade, descarta veículos de fora e marca o padrão em todas as remessas
            const vehicles = [...(payload as ISubmissionVehicleResponse[])].sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault));
            return {
                ...state,
                vehicles,
                vehiclesLoading: false,
                drafts: state.drafts.map((d) => ({ ...d, vehicleIds: withDefaultVehicles(d.vehicleIds, vehicles) })),
            };
        }
        case ACTION_TYPES.sendStart:
            return { ...state, sending: true, progress: 0, generalErrors: [] };
        case ACTION_TYPES.setProgress:
            return { ...state, progress: payload };
        case ACTION_TYPES.sendSuccess:
            return { ...state, sending: false, progress: 100, result: payload };
        case ACTION_TYPES.sendError: {
            const byItem: Record<number, string[]> = payload.byItem;
            const drafts = state.drafts.map((d, index) => ({ ...d, apiErrors: byItem[index] ?? [] }));
            const firstError = drafts.find((d) => d.apiErrors.length > 0);
            const next = { ...state, sending: false, progress: 0, drafts, generalErrors: payload.general };
            return firstError ? activate(next, firstError) : next;
        }
        case ACTION_TYPES.reset:
            return resetWithVehicles(state.vehicles);
        default:
            throw new Error(`Unhandled action type: ${type}`);
    }
};
